// bearbeitenBeziehungen.ts — Beziehungen im Bearbeiten-Modus der Akte
// („Allgemein" = Anlage-Formular, docs/analyse-akte-allgemein-gleich-anlage.md, C3).
//
// Laden: aus einem geladenen Kontakt die `rel_*`-Felder der Anlage-Masken,
// `rel_previousIds` / `rel_previousLabels` und die Vereine bzw. den Arbeitgeber
// erzeugen — wie `ConvertContactPage`, aber ohne Typwechsel.
//
// Speichern: die Sammlung wieder zusammensetzen und über `syncCollectedRelations`
// abgleichen (PATCH/POST/DELETE). Zwei Sicherungen gegenüber der Umwandlung:
//
//   1. `rel_previousIds` enthält NUR Beziehungen, die die Maske auch zeigt.
//      Alles andere (etwa „wird vertreten von", „ist Assistenz von") steht in
//      keiner Liste — stünde es im Ausgangsbestand, löschte der Abgleich es.
//   2. Jeder Eintrag merkt sich beim Laden seinen Fingerabdruck
//      (`bestandSignatur`). Unveränderte Einträge fasst der Abgleich nicht an.
//
// Die Metadaten (Beziehungs-Ids, Richtung, Fingerabdruck) liegen zusätzlich in
// `rel_bestand`, nach Eintrags-Id. Grund: Die Vertretungs-Karte baut ihre
// Einträge beim Ändern neu auf und verliert dabei alles außer id, Rollen und
// Ziel — ohne diese Ablage würde jede berührte Vertretung gelöscht und neu
// angelegt.

import type { FormState } from "./formUtils";
import { contactToRelationItems, flattenRelations, type VereinPrefill } from "./contactToRelationItems";
import { foListeAusKontakt } from "./foUserIds";
import {
  ownershipAusFormular,
  relationSignatur,
  syncCollectedRelations,
  type Ownership,
  type RelationSyncReport,
  type SharedAddress,
} from "../components/relationsInline/persistRelations";
import type {
  PersonFirmaItem,
  RelationCollections,
  RelKind,
} from "../components/relationsInline/types";
import { arbeitgeberSetzen } from "../services/arbeitgeberService";
import { attachMenschen } from "../services/menschenFuersUnternehmenService";
import { apiUrl } from "./api";
import { buildMenschenItems } from "../components/verantwortlichkeit/buildMenschenItems";

type AnyRec = Record<string, any>;
export type KontaktTyp = "Person" | "Firma";

const parseArr = (raw: unknown): AnyRec[] => {
  if (Array.isArray(raw)) return raw as AnyRec[];
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

const parseObj = (raw: unknown): AnyRec => {
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const p = JSON.parse(raw);
    return p && typeof p === "object" && !Array.isArray(p) ? p : {};
  } catch {
    return {};
  }
};

const idsOf = (item: AnyRec): number[] =>
  Array.isArray(item?.relationIds)
    ? item.relationIds.filter((n: unknown): n is number => typeof n === "number")
    : typeof item?.relationId === "number"
      ? [item.relationId]
      : [];

export const kontaktTypVon = (contact: AnyRec): KontaktTyp =>
  (contact?.type?.type ?? contact?.typ) === "Firma" ? "Firma" : "Person";

/**
 * Ein Verein als Person↔Firma-Eintrag — genau die Zeile, die die Anlage für die
 * Karte „Vereine und Mitgliedschaften" schreibt: Verein = src, Person = dest,
 * „Mensch fürs Unternehmen" mit der Funktion (ohne Funktion „Mitglied").
 *
 * Ohne Kontakt-Id und ohne neu erfassten Verein bleibt nur der Name im
 * Mitgliedschaften-Text — wie in der Anlage.
 */
/**
 * Die Mitgliedschaften einer FIRMA als Person↔Firma-Einträge. Die Firmenmaske
 * fragt nicht „Besteht eine Mitgliedschaft?" (die Karte heißt
 * „Mitgliedschaften") — ohne Funktion ist es deshalb immer „Mitglied".
 */
export function vereineAlsVerbindungen(vereine: VereinPrefill[]): PersonFirmaItem[] {
  return vereine
    .map((v) => vereinAlsPersonFirma({ ...v, hatMitgliedschaft: true }))
    .filter((v): v is PersonFirmaItem => v !== null);
}

export function vereinAlsPersonFirma(v: VereinPrefill): PersonFirmaItem | null {
  const ref =
    v.vereinContactId != null
      ? { ...(v.ref ?? {}), contactId: v.vereinContactId, type: "Firma" as const, displayName: v.vereinName || v.ref?.displayName }
      : v.isNew && v.ref
        ? v.ref
        : null;
  if (!ref) return null;
  const funktion = (v.funktion ?? "").trim() || (v.hatMitgliedschaft ? "Mitglied" : "");
  return {
    id: v.id,
    ref,
    relationId: v.relationId,
    relationIds: v.relationIds,
    srcId: v.srcId,
    destId: v.destId,
    verant: {
      isExtern: false,
      externalCompany: null,
      roles: [{ kind: "mensch", verantwortlichkeit: funktion }],
    },
    ...(typeof (v as AnyRec).bestandSignatur === "string"
      ? { bestandSignatur: (v as AnyRec).bestandSignatur }
      : {}),
  } as PersonFirmaItem;
}

/** FO (alle), BO und persönlicher Inhouse-Kontakt eines geladenen Kontakts. */
export function verantwortungVon(contact: AnyRec): Ownership {
  const t = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const fo = foListeAusKontakt(contact);
  return {
    ...(fo[0] ? { foUserId: fo[0] } : {}),
    ...(fo.length > 1 ? { foUserIds: fo } : {}),
    ...(t(contact?.boType) ? { boType: t(contact.boType) } : {}),
    ...(t(contact?.boUserId) ? { boUserId: t(contact.boUserId) } : {}),
    ...(t(contact?.boTeamId) ? { boTeamId: t(contact.boTeamId) } : {}),
    ...(t(contact?.inhouseContactUserId) ? { inhouseContactUserId: t(contact.inhouseContactUserId) } : {}),
  };
}

/**
 * Verantwortlichkeit für neue Unterkontakte im Bearbeiten-Modus: der geladene
 * Stand (`rel_ownership`), überschrieben von dem, was das Formular selbst
 * führt, und zuletzt vom Aufrufer — jeweils nur mit gesetzten Werten.
 */
export function ownershipFuerAbgleich(aktuell: FormState, vomAufrufer: Ownership = {}): Ownership {
  const gesetzt = (o: Ownership): Ownership =>
    Object.fromEntries(
      Object.entries(o).filter(([, v]) => (Array.isArray(v) ? v.length > 0 : typeof v === "string" && v.trim() !== "")),
    ) as Ownership;
  const merged: Ownership = {
    ...gesetzt(parseObj(aktuell.rel_ownership) as Ownership),
    ...gesetzt(ownershipAusFormular(aktuell as Record<string, unknown>)),
    ...gesetzt(vomAufrufer),
  };
  // Die FO-Liste gilt nur zum primären FO — passt sie nicht mehr, fällt sie weg.
  if (merged.foUserIds && merged.foUserIds[0] !== merged.foUserId) delete merged.foUserIds;
  return merged;
}

/** Die Listen, die der Abgleich je Kontakttyp verantwortet. */
const LISTEN: Record<KontaktTyp, Array<{ feld: string; kind: RelKind }>> = {
  Person: [
    { feld: "rel_household", kind: "household" },
    { feld: "rel_weitere", kind: "household" },
    { feld: "rel_assistenz", kind: "assistenz" },
    { feld: "rel_personFirma", kind: "personFirma" },
    { feld: "rel_vertretung", kind: "vertretung" },
  ],
  Firma: [{ feld: "rel_firmaConn", kind: "firmaConn" }],
};

/**
 * Geladener Kontakt → die Beziehungsfelder des Anlage-Formulars im
 * Bearbeiten-Modus. Das Ergebnis wird in den Formular-State gemischt (neben
 * `contactToPrivateFormState` / `contactToCompanyFormState`).
 */
export function beziehungenFuerBearbeiten(contact: AnyRec): Record<string, string> {
  const typ = kontaktTypVon(contact);
  const mainId: number = contact?.id;
  const rel = contactToRelationItems(contact, typ, { bearbeiten: true });

  const bestand: Record<string, AnyRec> = {};
  const previous: number[] = [];
  const merken = (kind: RelKind, item: AnyRec, alsEintrag: AnyRec = item) => {
    const ids = idsOf(alsEintrag);
    if (!ids.length) return;
    const bestandSignatur = relationSignatur(kind, mainId, alsEintrag);
    item.bestandSignatur = bestandSignatur;
    // Spiegelzeilen derselben Beteiligung (Firmenverbindungen) gehören in den
    // Ausgangsbestand: Wer den Eintrag entfernt, entfernt die Beteiligung —
    // also beide Zeilen. Solange er sie meint, hält der Abgleich sie
    // (`behalteneSpiegel`).
    const spiegel = Array.isArray(alsEintrag.spiegel) ? alsEintrag.spiegel : [];
    bestand[String(item.id)] = {
      relationId: alsEintrag.relationId,
      relationIds: ids,
      srcId: alsEintrag.srcId,
      destId: alsEintrag.destId,
      bestandSignatur,
      ...(spiegel.length ? { spiegel } : {}),
    };
    previous.push(...ids, ...spiegel.map((sp: AnyRec) => sp.id).filter((n: unknown): n is number => typeof n === "number"));
  };

  const felder: Record<string, AnyRec[]> = {
    rel_household: rel.rel_household,
    rel_weitere: rel.rel_weitere,
    rel_assistenz: rel.rel_assistenz,
    rel_personFirma: rel.rel_personFirma,
    rel_vertretung: rel.rel_vertretung,
    rel_firmaConn: rel.rel_firmaConn,
  };
  for (const { feld, kind } of LISTEN[typ]) {
    for (const item of felder[feld]) merken(kind, item);
  }
  // Vereins-Mitgliedschaften — bei der Person wie bei der Firma (02.10.2026).
  for (const v of rel.vereine) {
    const alsEintrag = typ === "Firma" ? (vereineAlsVerbindungen([v])[0] ?? null) : vereinAlsPersonFirma(v);
    if (alsEintrag) merken("personFirma", v, alsEintrag);
  }
  if (typ !== "Person") {
    // Menschen fürs Unternehmen verwaltet der attach-menschen-Endpoint. Ihre
    // Ids gehören trotzdem in den Ausgangsbestand: Die Firmenmaske nimmt die
    // der noch gelisteten Personen selbst heraus — wer entfernt wurde, wird so
    // gelöscht.
    for (const m of rel.rel_menschen) previous.push(...idsOf(m));
  }

  // Beschriftungen für den Bestätigungsdialog „diese Beziehungen entfallen".
  const labels: Record<string, string> = {};
  const inBestand = new Set(previous);
  for (const f of flattenRelations(contact)) {
    if (typeof f.relationId !== "number" || !inBestand.has(f.relationId)) continue;
    const name =
      `${f.other?.lastname ?? ""}${f.other?.firstName ? ", " + f.other.firstName : ""}`.trim() ||
      "Unbenannter Kontakt";
    labels[String(f.relationId)] = f.relType ? `${name} — ${f.relType}` : name;
  }

  return {
    rel_household: JSON.stringify(rel.rel_household),
    rel_weitere: JSON.stringify(rel.rel_weitere),
    rel_assistenz: JSON.stringify(rel.rel_assistenz),
    rel_personFirma: JSON.stringify(rel.rel_personFirma),
    rel_menschen: JSON.stringify(rel.rel_menschen),
    rel_firmaConn: JSON.stringify(rel.rel_firmaConn),
    rel_vertretung: JSON.stringify(rel.rel_vertretung),
    rel_kvGroupId: rel.rel_kvGroupId,
    rel_previousIds: JSON.stringify(Array.from(new Set(previous))),
    rel_previousLabels: JSON.stringify(labels),
    rel_bestand: JSON.stringify(bestand),
    // Verantwortlichkeit des Kontakts — neu angelegte Unterkontakte bekommen
    // dieselbe (User 28.09.2026). In der Akte steht sie im Kopf, nicht im
    // Formular; ohne diese Ablage entstünden sie ohne FO/BO.
    rel_ownership: JSON.stringify(verantwortungVon(contact)),
    vereine: JSON.stringify(rel.vereine),
    ...(typ === "Person"
      ? {
          employerContactId: rel.arbeitgeber ? String(rel.arbeitgeber.id) : "",
          employerContactName: rel.arbeitgeber?.name ?? "",
        }
      : {}),
  };
}

/**
 * Formular-State → Sammlung für `syncCollectedRelations`. Fehlende Metadaten
 * eines Eintrags (weil eine Karte ihn neu aufgebaut hat) kommen aus
 * `rel_bestand`; was der Eintrag selbst trägt, gewinnt.
 */
export function sammlungFuerAbgleich(formFields: FormState, typ: KontaktTyp): RelationCollections {
  const bestand = parseObj(formFields.rel_bestand);
  const mitBestand = (item: AnyRec): AnyRec => ({ ...(bestand[String(item?.id)] ?? {}), ...item });
  const liste = (feld: string) => parseArr(formFields[feld]).map(mitBestand);

  if (typ === "Firma") {
    // Firma: Verbindungen zu Firmen und — seit 02.10.2026 — die
    // Vereins-Mitgliedschaften (dieselbe Zeile wie bei der Person).
    return {
      firmaConn: liste("rel_firmaConn") as RelationCollections["firmaConn"],
      personFirma: vereineAlsVerbindungen(liste("vereine") as VereinPrefill[]),
    };
  }
  const vereine = liste("vereine")
    .map((v) => vereinAlsPersonFirma(v as VereinPrefill))
    .filter((v): v is PersonFirmaItem => v !== null);
  return {
    household: liste("rel_household") as RelationCollections["household"],
    weitere: liste("rel_weitere") as RelationCollections["weitere"],
    // Wie die Anlage: eine Assistenz ohne Kontakt und ohne Namen ist leer.
    assistenz: liste("rel_assistenz").filter(
      (a) => a.contactId || a.ref?.contactId || (a.lastName && String(a.lastName).trim()),
    ) as RelationCollections["assistenz"],
    personFirma: [...liste("rel_personFirma"), ...vereine] as RelationCollections["personFirma"],
    vertretung: liste("rel_vertretung") as RelationCollections["vertretung"],
  };
}

/**
 * Menschen fürs Unternehmen, die seit dem Laden neu dazukamen oder sich
 * geändert haben. Nur sie gehen an attach-menschen — ein unveränderter Eintrag
 * wird nicht neu geschrieben.
 */
export function geaenderteMenschen(geladen: FormState, aktuell: FormState): AnyRec[] {
  const vorher = new Map(parseArr(geladen.rel_menschen).map((m) => [String(m?.id), JSON.stringify(m)]));
  return parseArr(aktuell.rel_menschen).filter((m) => m?.ref && vorher.get(String(m?.id)) !== JSON.stringify(m));
}

/** Ausgangsbestand für den Abgleich (Firma: ohne die Zeilen von attach-menschen). */
export function ausgangsbestand(formFields: FormState, typ: KontaktTyp): number[] {
  const ids = (parseArr(formFields.rel_previousIds) as unknown[]).filter(
    (n): n is number => typeof n === "number",
  );
  if (typ !== "Firma") return ids;
  const menschen = new Set(parseArr(formFields.rel_menschen).flatMap((m) => idsOf(m)));
  return ids.filter((id) => !menschen.has(id));
}

/**
 * Den Arbeitgeber (P12) nur schreiben, wenn er sich geändert hat. Geleert heißt
 * „Verknüpfung lösen" (`null`), nicht „nichts tun".
 */
export async function arbeitgeberAbgleichen(
  contactId: number,
  geladen: FormState,
  aktuell: FormState,
  setzen: typeof arbeitgeberSetzen = arbeitgeberSetzen,
): Promise<boolean> {
  const alt = String(geladen.employerContactId ?? "").trim();
  const neu = String(aktuell.employerContactId ?? "").trim();
  if (alt === neu) return false;
  await setzen(contactId, neu ? Number(neu) : null);
  return true;
}

/**
 * Kundenverbindung (`rel_kvGroupId`) nur schreiben, wenn sie sich geändert hat.
 * Bis 28.09.2026 lief das im Bearbeiten-Modus gar nicht — die Auswahl stand in
 * der Karte, kam aber nie in der Datenbank an (nur die Neuanlage kannte
 * `assignKundenverbindung`).
 *
 *   neu gewählt / gewechselt → POST contactgroup/:neu/contacts (Mitglied + FK)
 *   gewechselt / geleert     → DELETE contactgroup/:alt/contacts/:kontakt
 *
 * Erst hinzufügen, dann entfernen: Scheitert das Hinzufügen, bleibt die alte
 * Verbindung stehen. Liefert Fehlertexte (leer = alles gut / nichts zu tun).
 */
export async function kundenverbindungAbgleichen(
  contactId: number,
  geladen: FormState,
  aktuell: FormState,
): Promise<string[]> {
  const alt = String(geladen.rel_kvGroupId ?? "").trim();
  const neu = String(aktuell.rel_kvGroupId ?? "").trim();
  if (alt === neu) return [];
  if (neu) {
    try {
      const res = await fetch(apiUrl(`contactgroup/${neu}/contacts`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok) return [`Die Kundenverbindung konnte nicht gesetzt werden (${res.status}).`];
    } catch {
      return ["Die Kundenverbindung konnte nicht gesetzt werden (keine Verbindung)."];
    }
  }
  if (alt) {
    try {
      const res = await fetch(apiUrl(`contactgroup/${alt}/contacts/${contactId}`), { method: "DELETE" });
      if (!res.ok && res.status !== 404) {
        return [`Die bisherige Kundenverbindung konnte nicht gelöst werden (${res.status}).`];
      }
    } catch {
      return ["Die bisherige Kundenverbindung konnte nicht gelöst werden (keine Verbindung)."];
    }
  }
  return [];
}

/**
 * Speichern der Beziehungen im Bearbeiten-Modus: (Firma) geänderte Menschen
 * fürs Unternehmen über `attach-menschen`, dann der Abgleich der Sammlung,
 * dann (Person) der Arbeitgeber.
 */
export async function beziehungenAbgleichen(args: {
  contactId: number;
  typ: KontaktTyp;
  geladen: FormState;
  aktuell: FormState;
  ownership: Ownership;
  householdAddress?: SharedAddress;
  householdGroupName?: string;
  /**
   * Der Arbeitgeber wird im Nachlauf des Formulars neu angelegt und verknüpft
   * (utils/anlageNachlauf.ts) — hier dann nicht vorher lösen.
   */
  arbeitgeberUeberspringen?: boolean;
  /** Nur für Tests: Ersatz für den attach-menschen-Aufruf. */
  attach?: typeof attachMenschen;
}): Promise<RelationSyncReport> {
  const { contactId, typ, geladen, aktuell } = args;
  const ownership = ownershipFuerAbgleich(aktuell, args.ownership);
  // Firma: Menschen fürs Unternehmen über attach-menschen (ersetzt die Rollen
  // je Paar transaktional). Bis 28.09.2026 lief das im Bearbeiten-Modus gar
  // nicht — ein im Popup geänderter oder neu erfasster Eintrag stand nach
  // „Übernehmen" in der Liste, kam aber nie in der Datenbank an.
  const fehlerMenschen: string[] = [];
  if (typ === "Firma") {
    const geaendert = geaenderteMenschen(geladen, aktuell);
    const items = geaendert.length ? buildMenschenItems(geaendert as any, contactId) : [];
    if (items.length) {
      try {
        await (args.attach ?? attachMenschen)(items, true);
      } catch {
        fehlerMenschen.push(
          "Die Verantwortung fürs Unternehmen konnte nicht gespeichert werden.",
        );
      }
    }
  }
  const report = await syncCollectedRelations(
    contactId,
    sammlungFuerAbgleich(aktuell, typ),
    // Nach einem gescheiterten attach-menschen nichts löschen — sonst wären die
    // alten Rollen weg und die neuen nie geschrieben.
    fehlerMenschen.length ? [] : ausgangsbestand(aktuell, typ),
    ownership,
    { householdAddress: args.householdAddress, householdGroupName: args.householdGroupName },
  );
  report.failures.push(...fehlerMenschen);
  report.failures.push(...(await kundenverbindungAbgleichen(contactId, geladen, aktuell)));
  if (typ === "Person" && !args.arbeitgeberUeberspringen) {
    try {
      await arbeitgeberAbgleichen(contactId, geladen, aktuell);
    } catch {
      report.failures.push("Der Arbeitgeber konnte nicht gespeichert werden.");
    }
  }
  return report;
}
